import React, { useEffect } from 'react';
import { cn } from '@core/ui';
import { useAutoGrow } from './useAutoGrow';

export interface TitleInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Called on Enter — move focus to the next content field (description). */
  onEnter?: () => void;
  /** Focus this field when it mounts (create dialogs, composers). */
  initialFocus?: boolean;
  /** Called on blur — field-level validation (e.g. "name is required"). */
  onBlur?: () => void;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /**
   * `md` (default) suits a dialog; `lg` suits a full-page composer where the
   * title is the loudest thing on screen.
   */
  size?: 'md' | 'lg';
}

/**
 * Borderless ghost-placeholder title field for create dialogs and composers.
 * The dialog is the box — the title doesn't get its own. Wraps instead of
 * scrolling; Enter jumps to the next field instead of inserting a newline.
 * See docs/ux/14-forms.md.
 */
export function TitleInput({
  value,
  onChange,
  placeholder,
  onEnter,
  initialFocus,
  onBlur,
  className,
  id,
  'aria-label': ariaLabel,
  size = 'md',
}: TitleInputProps) {
  const ref = useAutoGrow(value);

  useEffect(() => {
    if (initialFocus) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      onEnter?.();
    }
  };

  return (
    <textarea
      ref={ref}
      id={id}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      placeholder={placeholder}
      data-autofocus={initialFocus ? '' : undefined}
      autoCapitalize="off"
      aria-label={ariaLabel || placeholder}
      className={cn(
        'w-full resize-none overflow-hidden bg-transparent font-semibold',
        size === 'lg' ? 'text-2xl leading-snug' : 'text-lg',
        'text-foreground placeholder:text-muted-foreground/60 focus:outline-none',
        className
      )}
    />
  );
}
